import { Popover, TextInput } from "@mantine/core";
import { Link, useParams } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useMembership } from "../../../platform/membership";
import { managesMembers } from "../../../platform/permissions";
import { InviteForm } from "../../../platform/teams/InviteForm";
import { MemberList } from "../../../platform/teams/MemberList";
import { TeamCaseSwitch } from "../../../platform/teams/TeamCaseSwitch";
import { MarketingConsent, TeamNameHeader } from "../../../platform/teams/TeamHeader";
import page from "../actors/actors.module.css";
import create from "../create/create.module.css";
import gen from "../generations/generations.module.css";
import home from "../home2.module.css";
import library from "../library/library.module.css";
import { SoonControl } from "../Soon";
import classes from "./settings.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/home2/settings";

const P = "/teams/$teamId/brands/$brandId/home2/settings";

/**
 * The prototype's tabs, in its order, then the Account page's other tab and Settings' Access
 * tokens, which it does not draw (spec ui-perf/ui-switch-classic-simple-pro, PR e5a); the ones
 * with no page yet are coming soon.
 */
const TABS = [
  { label: "Team", to: P },
  { label: "Plans and credits", to: null },
  { label: "Integrations", to: null },
  { label: "Account", to: `${P}/account` },
  { label: "Appearance", to: `${P}/appearance` },
  { label: "Achievements", to: `${P}/achievements` },
  { label: "Access tokens", to: `${P}/tokens` },
] as const;

interface SettingsFrameProps {
  teamId: string;
  brandId: string;
  /** The body in the Account page's 640px column, as its tabs are drawn there. */
  narrow?: boolean | undefined;
  children: ReactNode;
}

/**
 * Pro's Settings: its title and its tabs, each a page of its own (the open one current, by its
 * exact path: `settings` begins every tab's), over the open tab's body.
 */
export function ProSettingsFrame(props: SettingsFrameProps) {
  const { teamId, brandId, narrow = false, children } = props;
  return (
    <div className={create.page}>
      <div className={page.head}>
        <div className={page.titles}>
          <h1 className={page.title}>Settings</h1>
        </div>
      </div>
      <nav className={`${page.tabs} ${classes.tabs}`} aria-label="Settings tabs">
        {TABS.map(({ label, to }) =>
          to === null ? (
            <SoonControl key={label} className={page.tab}>
              {label}
            </SoonControl>
          ) : (
            <Link
              key={label}
              to={to}
              params={{ teamId, brandId }}
              className={page.tab}
              activeOptions={{ exact: true }}
            >
              {label}
            </Link>
          ),
        )}
      </nav>
      {narrow ? <div className={classes.narrow}>{children}</div> : children}
    </div>
  );
}

/**
 * The prototype's `#settings-team`: the team's name, its members and their pending invitations,
 * inviting one, all the Members page's own pieces (`platform/teams`), drawn in the prototype's
 * page; its other tabs come later (spec ui-perf/pro-studio-redesign, PR d6, decision 33).
 */
export function SettingsPage() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const me = useMembership(teamId);
  const [search, setSearch] = useState("");
  const [inviting, setInviting] = useState(false);
  // The Members page's status line, for what was just sent: the app shows no toasts.
  const [notice, setNotice] = useState<string | undefined>(undefined);
  // The team layout renders this only for a team in the scope.
  if (me === undefined) return null;
  const manages = managesMembers(me.role);
  return (
    <ProSettingsFrame teamId={teamId} brandId={brandId}>
      <div className={classes.team}>
        <div className={classes.name}>
          <TeamNameHeader teamId={teamId} canRename={manages} />
          <MarketingConsent />
        </div>
        {/* The Members page's word for it, until billing exists. */}
        <span className={home.small}>Billing period: coming soon</span>
      </div>
      <div className={gen.toolbar}>
        <TextInput
          className={library.search}
          aria-label="Search members"
          placeholder="Search members"
          leftSection={<Search size={16} aria-hidden />}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
        <span className={create.grow} />
        {manages ? (
          <>
            <SoonControl className={library.secondary}>Set credit limits</SoonControl>
            <Popover
              width={340}
              position="bottom-end"
              opened={inviting}
              onChange={setInviting}
              trapFocus
            >
              <Popover.Target>
                <button
                  type="button"
                  className={library.primary}
                  onClick={() => {
                    setNotice(undefined);
                    setInviting((open) => !open);
                  }}
                >
                  Invite member
                </button>
              </Popover.Target>
              <Popover.Dropdown p={24} aria-label="Invite member">
                <InviteForm
                  teamId={teamId}
                  myRole={me.role}
                  onSent={() => {
                    setInviting(false);
                    setNotice("Invite sent successfully");
                  }}
                  onCancel={() => setInviting(false)}
                />
              </Popover.Dropdown>
            </Popover>
          </>
        ) : null}
      </div>
      {notice === undefined ? null : (
        <output className={home.small}>{notice}</output>
      )}
      {/* Its words wrap on a narrow screen, so every column stays in view (decision 37). */}
      <div className={classes.table}>
        <MemberList teamId={teamId} me={me} search={search} onNotice={setNotice} scroll />
      </div>
      {manages ? <TeamCaseSwitch teamId={teamId} /> : null}
    </ProSettingsFrame>
  );
}
